import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { RefreshCw } from 'lucide-react'
import type { EtaConfidence, EtaKestrel, EtaResponse } from '@rapid-response/shared'
import { getEta } from '~/server/eta'
import { EtaMap } from '~/components/eta/EtaMap'
import { PageHeader } from '~/components/PageHeader'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Switch } from '~/components/ui/switch'
import { fmtDuration, fmtExecutionStatus, fmtMiles, lastSeenLabel, partnerContactsUrl } from '~/lib/dispatch-format'
import {
  CONFIDENCE_META,
  FACTOR_LABELS,
  KESTREL_COLOR,
  fmtEtaClock,
  fmtFactorPct,
  fmtDiscardedReadings,
  fmtGapMetres,
  fmtKestrelFreshness,
  kestrelDriveNote,
  kestrelMatchLabel,
} from '~/lib/eta-format'
import { jobSearchSchema } from '~/lib/job-search'

export const Route = createFileRoute('/eta')({
  validateSearch: jobSearchSchema,
  component: EtaTracker,
})

const AUTO_REFRESH_MS = 30_000

function EtaTracker() {
  const { job: jobNumber } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [jobInput, setJobInput] = useState(jobNumber ?? '')
  const [eta, setEta] = useState<EtaResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(false)
  // Only the latest job's response may update the page; a slower one for a
  // job the user has moved away from is dropped.
  const latestJob = useRef<string | null>(null)

  const load = useCallback(async (job: string) => {
    latestJob.current = job
    const isLatest = () => latestJob.current === job
    setLoading(true)
    try {
      const res = await getEta({ data: { jobNumber: job } })
      if (isLatest()) setEta(res)
    } catch (err) {
      if (!isLatest()) return
      setEta(null)
      toast.error(err instanceof Error ? err.message : 'Could not track this job')
    } finally {
      if (isLatest()) setLoading(false)
    }
  }, [])

  function onSearch(e: FormEvent) {
    e.preventDefault()
    const job = jobInput.trim()
    if (!job) {
      toast.error('Enter a valid job number')
      return
    }
    if (job === jobNumber) load(job)
    else navigate({ search: { job } })
  }

  // The URL is the source of truth, so deep links and back/forward both track.
  useEffect(() => {
    setJobInput(jobNumber ?? '')
    setEta(null)
    if (jobNumber == null) {
      latestJob.current = null
      setLoading(false)
    } else {
      load(jobNumber)
    }
  }, [jobNumber, load])

  // Poll while auto-refresh is on and a job is being tracked.
  useEffect(() => {
    if (!autoRefresh || jobNumber == null) return
    const handle = setInterval(() => load(jobNumber), AUTO_REFRESH_MS)
    return () => clearInterval(handle)
  }, [autoRefresh, jobNumber, load])

  return (
    <div className="space-y-4">
      <PageHeader
        title="ETA Tracker"
        description="Enter a job number to track its allocated officer's live ETA and whether they're genuinely en route."
      />

      <form onSubmit={onSearch} className="flex max-w-md gap-2">
        <Input
          type="text"
          placeholder="Enter job number… (e.g. J21303142)"
          value={jobInput}
          onChange={(e) => setJobInput(e.target.value)}
        />
        <Button type="submit" disabled={loading}>
          {loading && eta === null ? 'Tracking…' : 'Track'}
        </Button>
      </form>

      {jobNumber != null && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch id="auto" checked={autoRefresh} onCheckedChange={setAutoRefresh} />
            <label htmlFor="auto">Auto-refresh every 30s</label>
          </div>
          <Button size="sm" variant="outline" disabled={loading} onClick={() => load(jobNumber)}>
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      )}

      {eta && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[380px_1fr]">
          <div className="space-y-4">
            <OfficerCard eta={eta} />
            {eta.kestrel && <KestrelCard kestrel={eta.kestrel} />}
            <ConfidenceCard confidence={eta.confidence} />
          </div>
          <div className="h-[70vh] overflow-hidden rounded-md border">
            <EtaMap eta={eta} />
          </div>
        </div>
      )}

      {!eta && jobNumber != null && !loading && (
        <p className="text-sm text-muted-foreground">No ETA to show for this job.</p>
      )}
    </div>
  )
}

function OfficerCard({ eta }: Readonly<{ eta: EtaResponse }>) {
  const { officer, drive } = eta
  const seen = lastSeenLabel(eta.lastSeen)
  return (
    <div className="space-y-3 rounded-md border p-4">
      <div>
        <div className="text-sm font-semibold">
          {officer.firstName} {officer.lastName}
        </div>
        <div className="text-xs text-muted-foreground">
          {officer.partnerName}
          {officer.phone ? ` · ${officer.phone}` : ''}
        </div>
      </div>

      <DriveStats drive={drive} />

      <div className="flex flex-wrap gap-1">
        <Badge variant="outline">{fmtExecutionStatus(eta.executionStatus)}</Badge>
        {seen && <Badge variant="outline">Seen {seen}</Badge>}
      </div>

      {officer.partnerId && (
        <a
          href={partnerContactsUrl(officer.partnerId)}
          target="_blank"
          rel="noreferrer"
          className="block text-xs font-semibold !text-[#FA5F00] hover:!text-[#c94d00] hover:underline"
        >
          View partner contacts
        </a>
      )}
    </div>
  )
}

function DriveStats({ drive }: Readonly<{ drive: EtaResponse['drive'] }>) {
  return (
    <div className="flex flex-wrap gap-2">
      <Stat label="ETA" value={drive.durationSeconds == null ? '—' : fmtDuration(drive.durationSeconds)} />
      <Stat label="Arrival" value={fmtEtaClock(drive.etaIso)} />
      <Stat label="By road" value={fmtMiles(drive.distanceMiles)} />
    </div>
  )
}

function KestrelCard({ kestrel }: Readonly<{ kestrel: EtaKestrel }>) {
  const seen = lastSeenLabel(kestrel.lastSeen)
  const confidence = kestrel.confidence
  const meta = confidence ? CONFIDENCE_META[confidence.label] : null
  const driveNote = kestrelDriveNote(kestrel)
  return (
    <div className="space-y-3 rounded-md border p-4" style={{ borderColor: KESTREL_COLOR }}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: KESTREL_COLOR }}>
          Kestrel comparison
        </span>
        {confidence && meta && (
          <Badge className={meta.badgeClass}>
            {meta.label} · {confidence.score}
          </Badge>
        )}
      </div>

      {kestrel.unavailableReason && <p className="text-sm text-muted-foreground">{kestrel.unavailableReason}</p>}
      {kestrel.drive && <DriveStats drive={kestrel.drive} />}
      {driveNote && <p className="text-sm text-muted-foreground">{driveNote}</p>}
      {kestrel.currentLocation && (
        <div className="flex flex-wrap gap-2">
          <Stat label="Accuracy" value={kestrel.accuracyM == null ? '—' : `${Math.round(kestrel.accuracyM)} m`} />
          <Stat label="Gap from EGIS" value={fmtGapMetres(kestrel.gapMetres)} />
        </div>
      )}

      <div className="flex flex-wrap gap-1">
        <Badge variant="outline">{kestrelMatchLabel(kestrel)}</Badge>
        {kestrel.freshness && <Badge variant="outline">{fmtKestrelFreshness(kestrel.freshness)}</Badge>}
        {seen && <Badge variant="outline">Seen {seen}</Badge>}
        {kestrel.discardedReadings > 0 && (
          <Badge variant="outline">{fmtDiscardedReadings(kestrel.discardedReadings)}</Badge>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="min-w-[84px] rounded-md border bg-muted/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  )
}

function ConfidenceCard({ confidence }: Readonly<{ confidence: EtaConfidence }>) {
  const meta = CONFIDENCE_META[confidence.label]
  return (
    <div className="space-y-3 rounded-md border p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          En-route confidence
        </span>
        <Badge className={meta.badgeClass}>
          {meta.label} · {confidence.score}
        </Badge>
      </div>

      <div className="space-y-2">
        {(Object.keys(FACTOR_LABELS) as (keyof typeof confidence.factors)[]).map((key) => (
          <FactorBar key={key} label={FACTOR_LABELS[key]} value={confidence.factors[key]} color={meta.color} />
        ))}
      </div>

      <ul className="space-y-1 border-t pt-2 text-xs text-muted-foreground">
        {confidence.reasons.map((reason) => (
          <li key={reason}>• {reason}</li>
        ))}
      </ul>
    </div>
  )
}

function FactorBar({ label, value, color }: Readonly<{ label: string; value: number; color: string }>) {
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)))
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{fmtFactorPct(value)}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  )
}
